import React, { useEffect, useState } from "react";
import TeleconsultCard from "../../components/Cards/TeleconsultCard";
import { ConsultWithParticipants } from "../../components/interface/consult";
import { getConsults } from "../../services/apiService";
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import TeleconsultCardTwo from "../../components/Cards/TeleconsultCardTwo";

interface TeleconsultHistoryProps {
  patientId: string;
}

const TeleconsultHistory: React.FC<TeleconsultHistoryProps> = ({ patientId }) => {
  const [history, setHistory] = useState<ConsultWithParticipants[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0); // 0-based index
  const [rowsPerPage, setRowsPerPage] = useState<number>(9);
  const [totalPages, setTotalPages] = useState<number>(1);

  const fetchPatientHistory = async (page: number, limit: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await getConsults(patientId, page + 1, limit); // API uses 1-based indexing
      const fetchedHistory = data.data.consults.map((historyItem: any) => ({
        ...historyItem,
        participants: {
          publisher: historyItem.participants?.filter((p: any) => p.role === "publisher"),
          subscriber: historyItem.participants?.filter((p: any) => p.role === "subscriber"),
        },
      }));

      setHistory(fetchedHistory);
      setTotalPages(data.data.pagination.last_page);
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to fetch teleconsult history.");
      console.error("Error details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientHistory(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(0);
  };
  return (
    <div className="p-4 m-6 bg-white rounded-lg shadow-xl">
      {loading && (
        <div className="flex justify-center items-center h-auto bg-[#eaeaea]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
      )}
      {error && <p className="text-red-500">{error}</p>}
      {!loading && !error && history.length > 0 && (
        <>
          {/* Scrollable Teleconsult Cards Section */}
          <div className="scroll-container flex-grow overflow-y-auto max-h-[calc(93vh-150px)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {history.map((historyItem) => (
                <TeleconsultCardTwo
                  key={historyItem.id}
                  teleconsultHistory={historyItem}
                  participantName=""
                />
              ))}
            </div>
          </div>
  
          {/* Pagination Controls */}
          <div className="mt-2 flex flex-col sm:flex-row justify-end items-end">
            <div className="mb-2 sm:mb-0">
              <label htmlFor="rowsPerPage">Rows per page:</label>
              <select
                id="rowsPerPage"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="ml-2 border rounded p-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
              </select>
            </div>
  
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 0}
                className="text-sm disabled:opacity-50"
              >
                <FirstPageIcon />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <NavigateBeforeIcon />
              </button>
              <span className="text-sm">
                Page {currentPage + 1} of {totalPages || "?"}
              </span>
  
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <NavigateNextIcon />
              </button>
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
                className="px-3 py-1 text-sm disabled:opacity-50"
              >
                <LastPageIcon />
              </button>
            </div>
          </div>
        </>
      )}
      {!loading && !error && history.length === 0 && <p>No teleconsult history found.</p>}
    </div>
  );
  
};

export default TeleconsultHistory;
